<script lang="ts">
  import { page } from "$app/state";

  import CornerDownLeftIcon from "@lucide/svelte/icons/corner-down-left";
  import Loader2 from "@lucide/svelte/icons/loader-circle";
  import { Command } from "bits-ui";

  import { findCurrentPageHref } from "$lib/command-palette/utils/current-page";
  import type {
    CommandGroup,
    CommandItem,
  } from "$lib/command-palette/utils/groups";
  import { highlightMatches } from "$lib/command-palette/utils/highlight";
  import Button from "$lib/design-systems/product/primitives/buttons/Button.svelte";
  import Chip from "$lib/design-systems/product/primitives/chips/Chip.svelte";
  import NewChip from "$lib/design-systems/product/primitives/chips/NewChip.svelte";

  type Props = {
    groups: CommandGroup[];
    /** Current search; its words are set in medium weight within each row. */
    query?: string;
    onSelect: (item: CommandItem) => void;
    navigatingHref: string | null;
    /** Replaces the empty state while async results are being fetched. */
    loadingHint?: string | null;
    /** Empty-state message, e.g. "No results in Settings". */
    emptyTitle?: string;
    /** Optional action under the empty-state message, e.g. "Search all". */
    emptyAction?: { label: string; onclick: () => void };
  };

  let {
    groups,
    query = "",
    onSelect,
    navigatingHref,
    loadingHint = null,
    emptyTitle = "No results found",
    emptyAction,
  }: Props = $props();

  let currentPageHref = $derived(
    findCurrentPageHref(groups, page.url.pathname, page.url.origin),
  );
</script>

<!-- Kept on one line: any whitespace between these blocks renders as a
     space inside the label, splitting words around the highlight. -->
<!-- prettier-ignore -->
{#snippet highlighted(text: string)}{#each highlightMatches(text, query) as segment, i (i)}{#if segment.match}<span class="font-medium">{segment.text}</span>{:else}{segment.text}{/if}{/each}{/snippet}

<Command.List class="h-[440px] min-h-0 shrink overflow-y-auto pt-2 pb-3">
  {#if groups.length === 0}
    <div
      class="flex h-full flex-col items-center justify-center gap-4 px-6 text-center text-sm"
    >
      {#if loadingHint}
        <span class="text-tertiary">{loadingHint}</span>
      {:else}
        <span class="text-primary">{emptyTitle}</span>
        {#if emptyAction}
          <!-- mousedown is prevented so the search input keeps focus. -->
          <Button
            variant="primary"
            size="small"
            onmousedown={(event: MouseEvent) => event.preventDefault()}
            onclick={emptyAction.onclick}
          >
            {emptyAction.label}
          </Button>
        {/if}
      {/if}
    </div>
  {/if}

  {#each groups as group (group.heading)}
    <Command.Group class="mb-1 last:mb-0">
      {#if group.heading !== null}
        <Command.GroupHeading
          class="px-6 py-2 text-xs font-medium text-secondary"
        >
          {group.heading}
        </Command.GroupHeading>
      {/if}
      <Command.GroupItems class="px-3">
        {#each group.items as item (`${item.href}:${item.label}`)}
          <Command.Item
            value={`${item.href}:${item.label}`}
            keywords={item.keywords}
            disabled={item.disabled ?? false}
            onSelect={() => onSelect(item)}
            class="group flex cursor-pointer items-center gap-3 rounded-sm px-3 py-2.5 text-sm text-primary aria-[disabled=true]:opacity-50 data-[selected]:bg-primary-hover"
          >
            {#if navigatingHref === item.href}
              <Loader2 size={16} class="shrink-0 animate-spin icon-secondary" />
            {:else}
              <item.icon size={16} class="shrink-0 icon-secondary" />
            {/if}
            {#if item.breadcrumb}
              <div class="min-w-0 flex-1">
                <div class="truncate">{@render highlighted(item.label)}</div>
                {#if item.breadcrumb.length}
                  <div class="mt-0.5 truncate text-xs text-secondary">
                    {@render highlighted(item.breadcrumb.join(" / "))}
                  </div>
                {/if}
              </div>
            {:else if item.render}
              <item.render.component {...item.render.props} />
            {:else}
              <!-- One flex item: the highlight splits the label into several
                   nodes, which the row's gap would otherwise space apart. -->
              <span class="truncate">{@render highlighted(item.label)}</span>
            {/if}
            {#if item.href === currentPageHref}
              <Chip variant="default">Current page</Chip>
            {:else if item.newUntilMs}
              <NewChip expiresAtMs={item.newUntilMs} />
            {/if}
            <div class="ml-auto flex shrink-0 items-center gap-1.5">
              {#if item.href !== currentPageHref && !item.newUntilMs}
                {#if item.selectHint}
                  <span
                    class="hidden text-xs text-primary group-data-[selected]:inline"
                  >
                    {item.selectHint}
                  </span>
                {/if}
                {#if item.selectIcon}
                  <item.selectIcon
                    size={14}
                    class="hidden icon-secondary group-data-[selected]:block"
                  />
                {/if}
                {#if item.trailingIcon}
                  <item.trailingIcon size={14} class="icon-secondary" />
                {/if}
              {/if}
              <!-- Enter hint on whichever row is highlighted, unless the row
                   has its own hint (e.g. "Open Docs"). -->
              {#if !item.selectHint}
                <kbd
                  class="hidden h-5 w-5 items-center justify-center rounded bg-secondary text-secondary group-data-[selected]:inline-flex"
                >
                  <CornerDownLeftIcon size={12} class="icon-secondary" />
                </kbd>
              {/if}
            </div>
          </Command.Item>
        {/each}
      </Command.GroupItems>
    </Command.Group>
  {/each}
</Command.List>
